import { useEffect, useState } from "react";
import api from "@/lib/api";
import useIsMobile from "@/lib/useIsMobile";
import { money } from "@/lib/ui";
import { FollowableName } from "@/components/FollowableName";
import { Loader2, TrendingUp, TrendingDown, ArrowUpRight, ArrowDownRight } from "lucide-react";

export default function Movers() {
  const m = useIsMobile();
  const [data, setData] = useState(null);

  useEffect(() => { api.get("/standings/movers").then((r) => setData(r.data)).catch(() => setData({ ready: false, climbers: [], fallers: [] })); }, []);

  const page = m ? "bg-[#0C1017] min-h-screen text-slate-200 pb-24" : "";
  const title = m ? "text-slate-100" : "text-[#14181F]";
  const card = m ? "bg-white/5 border-white/10" : "bg-white border-[#E4E7EB]";
  const sub = m ? "text-slate-400" : "text-slate-500";

  const Row = (r, up) => (
    <div key={`${r.abbr}-${r.name}`} data-testid={`mover-${r.abbr}-${r.name.replace(/\s/g, "-")}`}
      className={`flex items-center gap-3 px-3 py-2.5 border-t ${m ? "border-white/5" : "border-[#EEF0F2]"}`}>
      <span className={`shrink-0 w-7 h-7 rounded-full flex items-center justify-center ${up ? "bg-[#16A34A]/15 text-[#16A34A]" : "bg-[#C8000F]/15 text-[#C8000F]"}`}>
        {up ? <ArrowUpRight className="w-4 h-4" /> : <ArrowDownRight className="w-4 h-4" />}
      </span>
      <div className="flex-1 min-w-0">
        <div className={`font-semibold text-sm truncate ${title}`}><FollowableName name={r.name} /></div>
        <div className={`text-[11px] truncate ${sub}`}>{r.event} · now #{r.place}{r.prev_place && r.prev_place !== r.place ? ` (was #${r.prev_place})` : ""}</div>
      </div>
      <div className={`text-sm font-bold shrink-0 ${up ? "text-[#16A34A]" : "text-[#C8000F]"}`}>{up ? "+" : "−"}{money(Math.abs(r.delta))}</div>
    </div>
  );

  return (
    <div className={page} data-testid="movers-page">
      <div className={`px-4 sm:px-6 py-5 border-b ${m ? "border-white/10" : "border-[#E4E7EB]"}`}>
        <div className="flex items-center gap-2">
          <TrendingUp className="w-6 h-6 text-[#C8000F]" />
          <h1 className={`font-display text-2xl font-extrabold tracking-tight ${title}`}>The Movers</h1>
        </div>
        <p className={`text-sm mt-1 ${sub}`}>
          {data?.ready ? `Biggest earnings swings over the last ${data.window_days} day${data.window_days === 1 ? "" : "s"} across every PRCA event.`
            : "Who's climbing the standings and who's sliding."}
        </p>
      </div>

      <div className="p-4 sm:p-6 max-w-4xl">
        {data === null ? (
          <div className="py-16 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-[#C8000F]" /></div>
        ) : !data.ready ? (
          <div className={`rounded-xl border border-dashed p-6 text-center ${m ? "border-white/15 text-slate-400" : "border-[#D7DBE0] text-slate-500"}`} data-testid="movers-collecting">
            <TrendingUp className="w-8 h-8 mx-auto mb-2 text-[#C8000F]" />
            <div className={`font-semibold ${title}`}>Collecting the numbers</div>
            <p className="text-sm mt-1">We snapshot the standings every night. Climbers and fallers appear once we've logged a couple of days{data.snapshots ? ` (${data.snapshots} so far)` : ""}.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <section>
              <div className="flex items-center gap-2 mb-2"><TrendingUp className="w-4 h-4 text-[#16A34A]" /><h2 className={`font-display font-bold text-sm uppercase tracking-wide ${title}`}>Climbers</h2></div>
              <div className={`rounded-xl border overflow-hidden ${card}`}>
                {data.climbers.length ? data.climbers.map((r) => Row(r, true)) : <div className={`px-3 py-6 text-center text-sm ${sub}`}>No climbers in this window.</div>}
              </div>
            </section>
            <section>
              <div className="flex items-center gap-2 mb-2"><TrendingDown className="w-4 h-4 text-[#C8000F]" /><h2 className={`font-display font-bold text-sm uppercase tracking-wide ${title}`}>Fallers</h2></div>
              <div className={`rounded-xl border overflow-hidden ${card}`}>
                {data.fallers.length ? data.fallers.map((r) => Row(r, false)) : <div className={`px-3 py-6 text-center text-sm ${sub}`}>No fallers in this window.</div>}
              </div>
            </section>
          </div>
        )}
      </div>
    </div>
  );
}
